import { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import axios from "axios";
import { BrowserRouter } from "react-router-dom";

// UI components (shadcn)
import { Button } from "./components/ui/button.jsx";
import { Input } from "./components/ui/input.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card.jsx";
import { Badge } from "./components/ui/badge.jsx";
import { Label } from "./components/ui/label.jsx";
import { Separator } from "./components/ui/separator.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select.jsx";
import { Switch } from "./components/ui/switch.jsx";
import { Toaster } from "./components/ui/sonner.jsx";
import { toast } from "sonner";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose, DrawerTrigger } from "./components/ui/drawer.jsx";
import { Textarea } from "./components/ui/textarea.jsx";

// Icons
import { Mail, Globe, Phone, MapPin, Search, RefreshCcw, Star, Loader2, Plus, LockKeyhole } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXCEL_URL = "https://customer-assets.emergentagent.com/job_medical-lookup/artifacts/s6qxzhj5_The%20May%20Firm_Logo%20%281%29.png" // dummy use for lint, real constant below
// Real Excel import URL
const EXCEL_IMPORT_URL = "https://customer-assets.emergentagent.com/job_efb657a8-b214-4f15-be8f-115de2368446/artifacts/wqrurle4_Copy%20of%20PROVIDER%20BIBLE%20%281%29.xlsx";

function useProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ specialties: [], cities: [], total: 0, preferred_count: 0 });

  const fetchMeta = async () => {
    try {
      const { data } = await axios.get(`${API}/providers/meta`);
      setMeta(data);
    } catch (e) {
      console.error(e);
    }
  };

  const search = async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/providers`, { params });
      setProviders(data.items || []);
      return data;
    } catch (e) {
      console.error(e);
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const nlqSearch = async (query) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/providers/nlq_search`, { query });
      setProviders(data.items || []);
      if (data.applied_filters) {
        const f = data.applied_filters;
        const parts = [];
        if (f.specialty) parts.push(`specialty: ${f.specialty}`);
        if (f.city) parts.push(`city: ${f.city}`);
        if (typeof f.preferred === "boolean") parts.push(`preferred: ${f.preferred ? "yes" : "no"}`);
        toast.success(`Applied ${parts.length ? parts.join(", ") : "no filters"}`);
      }
      return data;
    } catch (e) {
      console.error(e);
      toast.error("Natural language search unavailable. Falling back to keyword search.");
      return await search({ q: query });
    } finally {
      setLoading(false);
    }
  };

  return { providers, loading, meta, fetchMeta, search, nlqSearch, setProviders };
}

function Header({ meta, onImport, importing, onNew, adminUnlocked, onUnlockClick }) {
  return (
    <div className="sticky top-0 z-30 backdrop-blur-xl bg-white/70">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="https://customer-assets.emergentagent.com/job_medical-lookup/artifacts/s6qxzhj5_The%20May%20Firm_Logo%20%281%29.png" alt="The May Firm" className="h-16 w-16 rounded-full object-cover ring-1 ring-black/10" />
          <div>
            <h1 className="font-semibold text-neutral-900 tracking-tight">The May Firm • Provider Finder</h1>
            <p className="text-xs text-neutral-500">{meta.total} total • {meta.preferred_count} preferred</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {adminUnlocked ? (
            <Button onClick={onNew}>
              <Plus className="h-4 w-4 mr-2" /> New Provider
            </Button>
          ) : (
            <Button variant="outline" onClick={onUnlockClick}>
              <LockKeyhole className="h-4 w-4 mr-2" /> Unlock admin
            </Button>
          )}
          <Button variant="outline" onClick={onImport} disabled={importing}>
            {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
            Re-import
          </Button>
        </div>
      </div>
    </div>
  );
}

function Filters({ meta, onSearch, loading }) {
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("__all__");
  const [city, setCity] = useState("__all__");
  const [preferred, setPreferred] = useState(false);

  return (
    <Card className="shadow-sm border border-neutral-200">
      <CardHeader>
        <CardTitle className="text-base">Search</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 md:col-span-4">
            <Label htmlFor="q">Keyword</Label>
            <div className="relative mt-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input id="q" placeholder="Name, email, phone, notes..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
            </div>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Label>Specialty</Label>
            <Select value={specialty} onValueChange={setSpecialty}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="All specialties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {meta.specialties.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Label>City</Label>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="All cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {meta.cities.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-12 md:col-span-2 flex items-end gap-3">
            <div className="flex items-center gap-2">
              <Switch id="preferred" checked={preferred} onCheckedChange={setPreferred} />
              <Label htmlFor="preferred">Preferred only</Label>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => onSearch({ q: q || undefined, specialty: specialty === "__all__" ? undefined : specialty, city: city === "__all__" ? undefined : city, preferred })} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            Search
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function NLQBox({ onAsk, loading }) {
  const [query, setQuery] = useState("");
  const example = "Find me a preferred Chiropractor in Bakersfield";

  const submit = () => {
    if (!query.trim()) {
      toast.message("Type a question first");
      return;
    }
    onAsk(query.trim());
  };

  return (
    <Card className="shadow-md border border-neutral-200 overflow-hidden">
      <CardHeader className="bg-neutral-50 border-b before:content-[''] before:block before:h-1 before:bg-[hsl(77_60%_45%)]" >
        <CardTitle className="text-base">Ask in natural language</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div className="flex gap-3 items-center">
          <Input placeholder={example} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} />
          <Button onClick={submit} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            Ask
          </Button>
        </div>
        <p className="text-xs text-neutral-500 mt-2">Powered by EMERGENT_LLM_KEY (add it to enable AI). Falls back to keyword search otherwise.</p>
      </CardContent>
    </Card>
  );
}

function ProviderCard({ p, onEdit, onDelete, adminUnlocked, onContact }) {
  const mail = p.email ? `mailto:${p.email}` : null;
  const tel = p.phone ? `tel:${p.phone}` : null;
  const website = p.website && /^https?:\/\//i.test(p.website) ? p.website : (p.website ? `https://${p.website}` : null);

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-lg leading-tight">{p.name || "Unknown"}</CardTitle>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-neutral-600">
              {p.specialty && <Badge variant="secondary">{p.specialty}</Badge>}
              {p.city && (
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{p.city}{p.state ? `, ${p.state}` : ""}</span>
              )}
              {p.preferred && <span className="inline-flex items-center gap-1 text-amber-600"><Star className="h-3 w-3 fill-amber-500" />Preferred</span>}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {p.notes && (
          <div>
            <span className="text-neutral-500">Notes:</span>
            <p className="text-neutral-800 mt-1 leading-relaxed whitespace-pre-wrap">{p.notes}</p>
          </div>
        )}
        <Separator />
        <div className="flex flex-wrap gap-2">
          {adminUnlocked && (
            <>
              <Button variant="secondary" size="sm" onClick={() => onEdit(p)}>
                Edit
              </Button>
              <Button variant="destructive" size="sm" onClick={() => onDelete(p)}>
                Delete
              </Button>
            </>
          )}
          <Button variant="default" size="sm" onClick={() => onContact(p)}>
            <Mail className="h-4 w-4 mr-1" /> Contact provider
          </Button>
          {mail && (
            <Button variant="outline" size="sm" asChild>
              <a href={mail} target="_blank" rel="noreferrer"><Mail className="h-4 w-4 mr-1" /> Email</a>
            </Button>
          )}
          {tel && (
            <Button variant="outline" size="sm" asChild>
              <a href={tel}><Phone className="h-4 w-4 mr-1" /> Call</a>
            </Button>
          )}
          {website && (
            <Button variant="outline" size="sm" asChild>
              <a href={website} target="_blank" rel="noreferrer"><Globe className="h-4 w-4 mr-1" /> Website</a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function App() {
  const [contactOpen, setContactOpen] = useState(false);
  const [contactProvider, setContactProvider] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", city: "", details: "", consent: false });
  const [sending, setSending] = useState(false);
  const [health, setHealth] = useState({ backend: null, checkedAt: null });

  const { providers, loading, meta, fetchMeta, search, nlqSearch, setProviders } = useProviders();
  const [importing, setImporting] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [adminRequired, setAdminRequired] = useState(false);

  // Drawer form state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null); // provider or null
  const [form, setForm] = useState({ name: "", specialty: "", city: "", state: "", email: "", phone: "", website: "", notes: "", preferred: false });

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", specialty: "", city: "", state: "", email: "", phone: "", website: "", notes: "", preferred: false });
    setDrawerOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name || "", specialty: p.specialty || "", city: p.city || "", state: p.state || "", email: p.email || "", phone: p.phone || "", website: p.website || "", notes: p.notes || "", preferred: !!p.preferred });
    setDrawerOpen(true);
  };

  const validateEmail = (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const normalizeWebsite = (val) => {
    if (!val) return "";
    if (/^https?:\/\//i.test(val)) return val;
    return `https://${val}`;
  };
  const validatePhone = (val) => !val || /[0-9]{7,}/.test(val.replace(/[^0-9]/g, ""));

  const submitForm = async (addAnother=false) => {
    if (!form.name?.trim() || !form.specialty?.trim()) {
      toast.error("Name and Specialty are required");
      return;
    }
    // basic validations
    if (!form.email?.trim() || !validateEmail(form.email)) { toast.error("Valid contact email is required"); return; }
    if (!validatePhone(form.phone)) { toast.error("Invalid phone number"); return; }
    const payload = { ...form, website: normalizeWebsite(form.website) };

    try {
      if (editing) {
        const { data } = await axios.patch(`${API}/providers/${editing.id}`, payload);
        setProviders((old) => old.map((x) => (x.id === editing.id ? data : x)));
        toast.success("Provider updated");
      } else {
        const { data } = await axios.post(`${API}/providers`, payload);
        setProviders((old) => [data, ...old]);
        toast.success("Provider created");
      }
      if (addAnother) {
        setEditing(null);
        setForm({ name: "", specialty: "", city: "", state: "", email: "", phone: "", website: "", notes: "", preferred: false });
      } else {
        setDrawerOpen(false);
      }
    } catch (e) {
      console.error(e);
      toast.error("Save failed");
    }
  };

  const handleDelete = async (prov) => {
    if (!window.confirm(`Delete ${prov.name || "this provider"}?`)) return;
    try {
      await axios.delete(`${API}/providers/${prov.id}`);
      toast.success("Provider deleted");
      setProviders((old) => old.filter((x) => x.id !== prov.id));
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete provider");
    }
  };

  const handleUnlock = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/needs_passcode`);
      if (!data.required) {
        setAdminUnlocked(true);
        setAdminRequired(false);
        localStorage.setItem("adminUnlocked", "1");
        return;
      }
      const pass = window.prompt("Enter admin passcode");
      if (pass === null) return;
      const res = await axios.post(`${API}/admin/verify`, { passcode: pass });
      if (res.data.ok) {
        setAdminUnlocked(true);
        setAdminRequired(true);
        localStorage.setItem("adminUnlocked", "1");
        toast.success("Admin unlocked");
      }
    } catch (e) {
      toast.error("Invalid passcode");
    }
  };

  const autoImport = async () => {
    try {
      const { data } = await axios.get(`${API}/providers/count`);
      if (data.count === 0 && !localStorage.getItem("providersImported")) {
        setImporting(true);
        const res = await axios.post(`${API}/providers/import_from_url`, { url: EXCEL_IMPORT_URL });
        toast.success(`Imported ${res.data.inserted} providers`);
        localStorage.setItem("providersImported", "1");
        await fetchMeta();
        await search({ limit: 50 });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setImporting(false);
    }
  };

  const manualImport = async () => {
    try {
      setImporting(true);
      const res = await axios.post(`${API}/providers/import_from_url`, { url: EXCEL_IMPORT_URL });
      toast.success(`Imported ${res.data.inserted} providers`);
      await fetchMeta();
      await search({ limit: 50 });
    } catch (e) {
      console.error(e);
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  // health check
  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/providers/meta`);
        if (data && typeof data.total === 'number') {
          setHealth({ backend: 'ok', checkedAt: new Date().toISOString() });
        } else {
          setHealth({ backend: 'degraded', checkedAt: new Date().toISOString() });
        }
      } catch (e) {
        setHealth({ backend: 'down', checkedAt: new Date().toISOString() });
      }
    };
    check();
  }, []);

  };

  useEffect(() => {
    fetchMeta();
    search({ limit: 50 });
    autoImport();
    // restore admin unlock if previously set
    if (localStorage.getItem("adminUnlocked") === "1") {
      setAdminUnlocked(true);
    }
  }, []);

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-[radial-gradient(1000px_600px_at_20%_-20%,rgba(10,10,10,0.06),transparent_60%),radial-gradient(1000px_600px_at_120%_-20%,rgba(10,10,10,0.06),transparent_60%)]">
        <Header meta={meta} onImport={manualImport} importing={importing} onNew={openNew} adminUnlocked={adminUnlocked} onUnlockClick={handleUnlock} />
        <main className="max-w-6xl mx-auto px-6 py-6 space-y-6">
          <NLQBox onAsk={nlqSearch} loading={loading} />
          <Filters meta={meta} onSearch={search} loading={loading} />

          <div className="flex items-center justify-between mt-2">
            <p className="text-sm text-neutral-600">Showing {providers.length} results</p>
            <div className="text-xs text-neutral-500">Tip: Try “Find me a preferred Chiropractor in Bakersfield”.</div>
          </div>

          {loading ? (
            <div className="py-16 text-center text-neutral-500">Loading…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {providers.map((p) => (
                <ProviderCard key={p.id} p={p} onEdit={openEdit} onDelete={handleDelete} adminUnlocked={adminUnlocked} onContact={(prov) => { setContactProvider(prov); setContactOpen(true); }} />
              ))}
              {providers.length === 0 && (
                <div className="text-neutral-500 py-16 text-center">No providers found. Adjust filters or import data.</div>
              )}
            </div>
          )}
        </main>

        {/* Contact Provider Drawer */}
        <Drawer open={contactOpen} onOpenChange={setContactOpen}>
          <DrawerContent className="max-h-[92vh]">
            <DrawerHeader className="border-b">
              <DrawerTitle>Contact provider</DrawerTitle>
            </DrawerHeader>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 text-sm text-neutral-600">
                {contactProvider && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{contactProvider.name || "Provider"}</span>
                    {contactProvider.specialty && <Badge variant="secondary">{contactProvider.specialty}</Badge>}
                    {contactProvider.city && <span className="text-neutral-500">{contactProvider.city}</span>}
                  </div>
                )}
              </div>
              {/* Provider email validity prompt */}
              {contactProvider && (!(contactProvider.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(contactProvider.email).trim())) ) && (
                <div className="md:col-span-2 p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-800 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">No valid contact email on file</div>
                    <div className="text-sm">This provider doesn’t have a valid contact email. Please edit the provider to add one before sending.</div>
                  </div>
                  {adminUnlocked && (
                    <Button variant="outline" size="sm" onClick={() => { setContactOpen(false); openEdit(contactProvider); }}>
                      Edit provider
                    </Button>
                  )}
                </div>
              )}

              <div>
                <Label>Patient Name *</Label>
                <Input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Jane Doe" />
                {!contactForm.name?.trim() && <p className="text-xs text-red-600 mt-1">Required</p>}
              </div>
              <div>
                <Label>Patient Email *</Label>
                <Input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="jane@example.com" />
                {(!contactForm.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) && <p className="text-xs text-red-600 mt-1">Valid email required</p>}
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="(555) 555-5555" />
              </div>
              <div>
                <Label>City</Label>
                <Input value={contactForm.city} onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })} placeholder="Bakersfield" />
              </div>
              <div className="md:col-span-2">
                <Label>Case Details *</Label>
                <Textarea rows={5} value={contactForm.details} onChange={(e) => setContactForm({ ...contactForm, details: e.target.value })} placeholder="Brief details about the case" />
                {!contactForm.details?.trim() && <p className="text-xs text-red-600 mt-1">Required</p>}
              </div>
              <div>
                <Label>Bcc (optional)</Label>
                <Input type="email" value={contactForm.bcc || ""} onChange={(e) => setContactForm({ ...contactForm, bcc: e.target.value })} placeholder="referrals@mayfirm.com" />
                {contactForm.bcc?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.bcc) && <p className="text-xs text-red-600 mt-1">Enter a valid email</p>}
                <p className="text-xs text-neutral-500 mt-1">Tip: add FV Project email</p>
              </div>
              <div className="md:col-span-2 flex items-center gap-3">
                <Switch id="consent" checked={contactForm.consent} onCheckedChange={(v) => setContactForm({ ...contactForm, consent: v })} />
                <Label htmlFor="consent">I have consent to share this information *</Label>
                {!contactForm.consent && <span className="text-xs text-red-600">Required</span>}
              </div>
            </div>
            <DrawerFooter>
              <div className="flex gap-3">
                <Button disabled={sending} onClick={async () => {
                  if (!contactProvider) { toast.error("Missing provider"); return; }
                  const valid = contactForm.name?.trim() && contactForm.details?.trim() && contactForm.consent && contactForm.email?.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email);
                  if (!valid) { toast.error("Please complete required fields"); return; }
                  try {
                    setSending(true);
                    const payload = {
                      provider_id: contactProvider.id,
                      provider_email: contactProvider.email,
                      provider_name: contactProvider.name,
                      specialty: contactProvider.specialty,
                      patient_name: contactForm.name,
                      patient_email: contactForm.email,
                      patient_phone: contactForm.phone || undefined,
                      patient_city: contactForm.city || undefined,
                      case_details: contactForm.details,
                      consent: true,
                      bcc_email: contactForm.bcc?.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.bcc) ? contactForm.bcc.trim() : undefined
                    };
                    const { data } = await axios.post(`${API}/emails/send_to_provider`, payload);
                    if (data.sent) {
                      toast.success("Email sent to provider");
                      setContactOpen(false);
                      setContactForm({ name: "", email: "", phone: "", city: "", details: "", consent: false });
                    } else {
                      toast.message("Request accepted. Check SendGrid/dashboard for status");
                    }
                  } catch (e) {
                    console.error(e);
                    toast.error("Failed to send email");
                  } finally {
                    setSending(false);
                  }
                }}>
                  {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Send email
                </Button>
                <DrawerClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DrawerClose>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerContent className="max-h-[92vh]">
            <DrawerHeader className="border-b">
              <DrawerTitle className="flex items-center gap-2">
                {!editing && <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-[hsl(77_60%_45%)/15%] text-[hsl(77_60%_45%)] border border-[hsl(77_60%_45%)/40%]">New</span>}
                {editing ? "Edit provider" : "New provider"}
              </DrawerTitle>
            </DrawerHeader>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>Specialty *</Label>
                {meta.specialties && meta.specialties.length > 0 && !form.__specialtyCustom ? (
                  <>
                    <Select
                      value={meta.specialties.includes(form.specialty) ? form.specialty : undefined}
                      onValueChange={(val) => {
                        if (val === "__custom__") {
                          setForm({ ...form, specialty: "", __specialtyCustom: true });
                        } else {
                          setForm({ ...form, specialty: val, __specialtyCustom: false });
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select specialty" />
                      </SelectTrigger>
                      <SelectContent>
                        {meta.specialties.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                        <SelectItem value="__custom__">Other (type manually)</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <>
                    <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="Type specialty" />
                    {meta.specialties && meta.specialties.length > 0 && (
                      <div className="mt-1 text-xs text-neutral-500">
                        <button type="button" className="underline" onClick={() => setForm({ ...form, __specialtyCustom: false })}>Use dropdown</button>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div>
                <Label>City</Label>
                {meta.cities && meta.cities.length > 0 && !form.__cityCustom ? (
                  <>
                    <Select
                      value={meta.cities.includes(form.city) ? form.city : undefined}
                      onValueChange={(val) => {
                        if (val === "__custom__") {
                          setForm({ ...form, city: "", __cityCustom: true });
                        } else {
                          setForm({ ...form, city: val, __cityCustom: false });
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select city" />
                      </SelectTrigger>
                      <SelectContent>
                        {meta.cities.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                        <SelectItem value="__custom__">Other (type manually)</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <>
                    <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Type city" />
                    {meta.cities && meta.cities.length > 0 && (
                      <div className="mt-1 text-xs text-neutral-500">
                        <button type="button" className="underline" onClick={() => setForm({ ...form, __cityCustom: false })}>Use dropdown</button>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div>
                <Label>State</Label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
              </div>
              <div>
                <Label>Contact Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                {(!form.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) && <p className="text-xs text-red-600 mt-1">Valid email required</p>}
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Website</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea rows={5} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="md:col-span-2 flex items-center gap-3">
                <Switch id="preferred_form" checked={form.preferred} onCheckedChange={(v) => setForm({ ...form, preferred: v })} />
                <Label htmlFor="preferred_form">Preferred</Label>
              </div>
            </div>
            <DrawerFooter>
              <div className="flex gap-3">
                <Button onClick={() => submitForm(false)}>
                  {editing ? "Save changes" : "Create"}
                </Button>
                {!editing && (
                  <Button variant="secondary" onClick={() => submitForm(true)}>
                    Create & Add Another
                  </Button>
                )}
                <DrawerClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DrawerClose>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        <Toaster position="top-right" />
        <footer className="mt-10 py-8 text-center text-xs text-neutral-500">
          <div>Build status: backend {health.backend || '…'}{health.checkedAt ? ` • checked ${new Date(health.checkedAt).toLocaleString()}` : ''}</div>
          <div className="mt-1">The May Firm • Provider Finder</div>
        </footer>
      </div>
    </BrowserRouter>
  );
}

export default App;